<template>
    <div>
        <limitless-page-header></limitless-page-header>

        <div class="content">
            <i v-if="busyLoading" class="icon-spinner2 spinner"></i>
            <div v-else-if="!busyLoading && report">
                <div v-if="voucherHasError" class="alert alert-danger">
                    Verifikatet är inte OK, se nedan...
                </div>
                <div class="card">
                    <div class="card-header header-elements-inline bg-light">
                        <h6 class="card-title">{{ reportName }}</h6>
                        <div class="header-elements d-flex flex-grow-1 justify-content-between pl-3">
                            <div>
                                <div v-if="report.approved_at" class="badge badge-success">Godkänd</div>
                                <div v-else class="badge badge-danger">Ej godkänd</div>
                                <div v-if="report.fortnox_id" :id="'tooltip-target-' + report.id" class="badge badge-success">Skickad till Fortnox</div>
                                <div v-else class="badge badge-danger" :id="'tooltip-target-' + report.id">Ej skickad till Fortnox</div>
                                <b-tooltip :target="'tooltip-target-' + report.id" triggers="hover" v-if="report.fortnox_id || report.fortnox_error">
                                    {{ report.fortnox_id ? report.fortnox_id : report.fortnox_error }}
                                </b-tooltip>
                            </div>
                            <div v-if="!report.approved_at && !dirty && !missingNetDebitSek">
                                <limitless-button
                                    @click="approveReport"
                                    icon="icon-checkmark4"
                                    :busy="busyApproving"
                                    :disabled="voucherHasError"
                                    ref="buttonApproveReport"
                                >Godkänn
                                </limitless-button>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <div class="col-lg-6">
                                <div><span class="font-weight-semibold">Datum: </span>{{ formatDateTime(report.from_date).substring(0, 10) }} - {{ formatDateTime(report.to_date).substring(0, 10) }}</div>
                            </div>
                            <div class="col-lg-6" v-if="report.approved_at">
                                <div><span class="font-weight-semibold">Godkänd: </span>{{ formatDateTime(report.approved_at) }}</div>
                                <div><span class="font-weight-semibold">Godkänd av: </span>{{ report.approved_by_user ? report.approved_by_user.name : '' }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    v-for="(group, groupIndex) in groupedRows"
                    :key="'g-' + groupIndex"
                    class="card"
                >
                    <div
                        v-if="groupIndex === 'sale'"
                        class="card-header header-elements-inline bg-light"
                    >
                        <h6 class="card-title">Försäljning</h6>
                        <div class="header-elements">
                            <button
                                type="button"
                                class="btn btn-light"
                                @click="showSaleDetails = !showSaleDetails"
                            >
                                {{ (showSaleDetails ? 'Dölj spec' : 'Visa spec') }}
                            </button>
                        </div>
                    </div>
                    <div
                        v-else-if="groupIndex === 'fee'"
                        class="card-header header-elements-inline bg-light"
                    >
                        <h6 class="card-title">Avgifter</h6>
                        <div class="header-elements">
                            <button
                                type="button"
                                class="btn btn-light"
                                @click="showFeeDetails = !showFeeDetails"
                            >
                                {{ (showFeeDetails ? 'Dölj spec' : 'Visa spec') }}
                            </button>
                        </div>
                    </div>
                    <div v-else class="card-header header-elements-inline bg-light">
                        <h6 class="card-title">{{ (groupIndex === 'payout' ? 'Utbetalning' : 'Avgifter') }}</h6>
                        <div class="header-elements">
                            <limitless-button
                                v-if="!report.approved_at"
                                @click="updateReport"
                                icon="icon-reload-alt"
                                :busy="busyUpdating"
                                ref="buttonUpdateReport"
                            >Uppdatera belopp
                            </limitless-button>
                        </div>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-xxs">
                            <thead>
                                <tr>
                                    <th v-if="groupIndex !== 'payout'">psp reference</th>
                                    <th v-if="groupIndex !== 'payout'">merchant reference</th>
                                    <th v-if="groupIndex !== 'payout'">payment method</th>
                                    <th>creation date</th>
                                    <th>type</th>
                                    <th>modification reference</th>
                                    <th>gross currency</th>
                                    <th class="text-right">gross debit</th>
                                    <th class="text-right">gross credit</th>
                                    <th class="text-right">exchange rate</th>
                                    <th>net currency</th>
                                    <th class="text-right">net debit</th>
                                    <th class="text-right">net credit</th>
                                    <th class="text-right">commission</th>
                                    <th class="text-right">markup</th>
                                    <th class="text-right">scheme fees</th>
                                    <th class="text-right">interchange</th>
                                    <th v-if="groupIndex !== 'payout'">payment method variant</th>
                                    <th v-if="groupIndex === 'payout'">net debit SEK</th>
                                    <th v-if="groupIndex === 'payout'">bank fee</th>
                                    <th v-if="groupIndex === 'payout'">fee rounding</th>
                                </tr>
                            </thead>
                            <tbody v-if="showDetails(groupIndex)">
                                <tr
                                    v-for="(row, rowIndex) in group.rows"
                                    :key="'g-' + groupIndex + '-r-' + rowIndex"
                                >
                                    <td v-if="groupIndex !== 'payout'">{{ row.psp_reference }}</td>
                                    <td v-if="groupIndex !== 'payout'">{{ row.merchant_reference }}</td>
                                    <td v-if="groupIndex !== 'payout'">{{ row.payment_method }}</td>
                                    <td>{{ formatDateTime(row.creation_date) }}</td>
                                    <td>{{ row.type }}</td>
                                    <td>{{ row.modification_reference }}</td>
                                    <td>{{ row.gross_currency }}</td>
                                    <td class="text-right">{{ formatNumber(row.gross_debit, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.gross_credit, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.exchange_rate, null, 10) }}</td>
                                    <td>{{ row.net_currency }}</td>
                                    <td class="text-right">{{ formatNumber(row.net_debit, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.net_credit, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.commission, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.markup, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.scheme_fees, 2) }}</td>
                                    <td class="text-right">{{ formatNumber(row.interchange, 2) }}</td>
                                    <th v-if="groupIndex !== 'payout'">{{ row.payment_method_variant }}</th>
                                    <th v-if="groupIndex === 'payout'">
                                        <span v-if="!report.approved_at"><b-form-input v-model="row.net_debit_sek"></b-form-input></span>
                                        <span v-else>{{ row.net_debit_sek }}</span>
                                    </th>
                                    <th v-if="groupIndex === 'payout'">
                                        <span v-if="!report.approved_at"><b-form-input v-model="row.bank_fee"></b-form-input></span>
                                        <span v-else>{{ row.bank_fee }}</span>
                                    </th>
                                    <th v-if="groupIndex === 'payout'">
                                        <span v-if="!report.approved_at"><b-form-input v-model="row.fee_rounding"></b-form-input></span>
                                        <span v-else>{{ row.fee_rounding }}</span>
                                    </th>
                                </tr>
                            </tbody>
                            <tfoot
                                v-for="(total, tIndex) in group.totals"
                                :key="'g-' + groupIndex + '-tot-' + tIndex"
                            >
                                <tr>
                                    <th :colspan="groupIndex !== 'payout' ? 6 : 3">
                                        Summering av {{ total.numRows }} rad(er)
                                    </th>
                                    <th>{{ total.gross_currency }}</th>
                                    <th class="text-right">{{ formatNumber(total.gross_debit, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.gross_credit, 2) }}</th>
                                    <th></th>
                                    <th>{{ total.net_currency }}</th>
                                    <th class="text-right">{{ formatNumber(total.net_debit, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.net_credit, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.commission, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.markup, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.scheme_fees, 2) }}</th>
                                    <th class="text-right">{{ formatNumber(total.interchange, 2) }}</th>
                                    <th :colspan="groupIndex === 'payout' ? 3 : 1"></th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
                <fortnox-voucher-preview
                    v-if="voucher || voucherError"
                    :voucher-error="voucherError"
                    :voucher="voucher"
                    @checked:error="voucherHasError = $event"
                ></fortnox-voucher-preview>
            </div>
            <div v-else>
                <i class="text-danger">Kunde inte hämta data...</i>
            </div>
        </div>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import { formatDateTime, formatNumber } from '@services/Helpers';
import { groupBy, sumBy } from 'lodash';
import FortnoxVoucherPreview from '@components/FortnoxVoucherPreview.vue';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader.vue';

export default {
    metaInfo() {
        return {
            title: this.$route.meta.name,
        };
    },
    components: {
        FortnoxVoucherPreview,
        LimitlessButton,
        LimitlessPageHeader,
    },
    data: () => ({
        busyLoading: true,
        busyUpdating: false,
        busyApproving: false,
        report: null,
        voucher: null,
        voucherError: null,
        voucherHasError: false,
        showSaleDetails: false,
        showFeeDetails: false,
        dirty: false,
    }),
    mounted() {
        this.getReport();
    },
    watch: {
        report: {
            deep: true,
            immediate: true,
            handler() {
                this.dirty = true;
            },
        },
    },
    methods: {
        formatNumber,
        formatDateTime,
        getReport() {
            this.busyLoading = true;
            apiClient.get('/vue/economy/report/adyen-settlement/' + this.$route.params.id)
                .then((response) => {
                    this.report = response.data.report;
                    this.voucher = response.data.voucher;
                    this.voucherError = response.data.voucher_error;
                })
                .finally(() => {
                    this.busyLoading = false;
                    this.dirty = false;
                });
        },
        async updateReport() {
            this.busyUpdating = true;
            const payloads = this.report.rows.filter((row) => row.type === 'MerchantPayout')
                .map((row) => ({
                    id: row.id,
                    net_debit_sek: row.net_debit_sek,
                    bank_fee: row.bank_fee,
                    fee_rounding: row.fee_rounding,
                }));

            await apiClient.post('/vue/economy/report/adyen-settlement/' + this.report.id, { rows: payloads })
                .then((response) => {
                    this.report = response.data.report;
                    this.voucher = response.data.voucher;
                    this.voucherError = response.data.voucher_error;
                })
                .catch(() => {
                    this.$refs.buttonUpdateReport.triggerFailed();
                })
                .finally(() => {
                    this.busyUpdating = false;
                    this.dirty = false;
                });
        },
        async approveReport() {
            this.busyApproving = true;
            await apiClient.post('/vue/economy/report/adyen-settlement/' + this.report.id + '/approve')
                .then((response) => {
                    this.report = response.data.report;
                    this.voucher = response.data.voucher;
                    this.voucherError = response.data.voucher_error;
                })
                .catch(() => {
                    this.$refs.buttonApproveReport.triggerFailed();
                })
                .finally(() => {
                    this.busyApproving = false;
                    this.dirty = false;
                });
        },
        showDetails(groupIndex) {
            if (groupIndex !== 'sale' && groupIndex !== 'fee') {
                return true;
            }
            if (groupIndex === 'sale' && this.showSaleDetails) {
                return true;
            }
            if (groupIndex === 'fee' && this.showFeeDetails) {
                return true;
            }
            return false;
        },
    },
    computed: {
        reportName() {
            return (this.report && this.report.id > 0 ? this.report.filename : '');
        },
        groupedRows() {
            let groupedRows = {};
            if (this.report && this.report.rows) {
                // eslint-disable-next-line
                groupedRows = groupBy(this.report.rows, 'type_group');
                Object.keys(groupedRows).forEach((key) => {
                    const groupedCurrencies = groupBy(groupedRows[key], (row) => {
                        if (row.gross_currency && row.gross_currency !== row.net_currency) {
                            return `${row.gross_currency}-${row.net_currency}`;
                        }
                        return row.net_currency;
                    });
                    const groupedTotals = {};
                    Object.keys(groupedCurrencies).forEach((currencyKey) => {
                        groupedTotals[currencyKey] = {
                            numRows: groupedCurrencies[currencyKey].length,
                            gross_currency: groupedCurrencies[currencyKey][0] ? groupedCurrencies[currencyKey][0].gross_currency : '',
                            net_currency: groupedCurrencies[currencyKey][0] ? groupedCurrencies[currencyKey][0].net_currency : '',
                            gross_debit: sumBy(groupedCurrencies[currencyKey], 'gross_debit'),
                            gross_credit: sumBy(groupedCurrencies[currencyKey], 'gross_credit'),
                            net_debit: sumBy(groupedCurrencies[currencyKey], 'net_debit'),
                            net_credit: sumBy(groupedCurrencies[currencyKey], 'net_credit'),
                            commission: sumBy(groupedCurrencies[currencyKey], 'commission'),
                            markup: sumBy(groupedCurrencies[currencyKey], 'markup'),
                            scheme_fees: sumBy(groupedCurrencies[currencyKey], 'scheme_fees'),
                            interchange: sumBy(groupedCurrencies[currencyKey], 'interchange'),
                        };
                    });
                    groupedRows[key] = {
                        rows: groupedRows[key],
                        totals: groupedTotals,
                    };
                });
            }
            return groupedRows;
        },
        missingNetDebitSek() {
            return !!this.groupedRows.payout?.rows?.filter((row) => row.net_debit_sek === null).length;
        },
    },
};
</script>
